<script lang="ts">
    import * as Sentry from "@sentry/svelte";
    import { onDestroy, onMount } from "svelte";
    import { computePosition, flip, shift, offset, autoUpdate } from "@floating-ui/dom";
    import type { Readable } from "svelte/store";
    import { AskPositionMessage_AskType } from "@workadventure/messages";
    import teleport from "../../images/teleport.svg";
    import businessCard from "../../images/business-cards.svg";
    import type { ChatUser } from "../../Connection/ChatConnection";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { scriptUtils } from "../../../Api/ScriptUtils";
    import { requestVisitCardsStore } from "../../../Stores/GameStore";
    import { wokaMenuStore } from "../../../Stores/WokaMenuStore";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { openModerationModal } from "../../../Components/Moderation/openModerationModal";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import type { UserProviderMerger } from "../../UserProviderMerger/UserProviderMerger";
    import WalkToIcon from "../../../Components/Icons/meta/WalkToIcon.svelte";
    import MapPinIcon from "../../../Components/Icons/meta/MapPinIcon.svelte";
    import UserAddIcon from "../../../Components/Icons/meta/UserAddIcon.svelte";
    import { userListStore, activeUserActionMenuStore } from "../../../Stores/UserListStore";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { openDirectChatRoom } from "../../Utils";
    import { triggerClickOnEnterOrSpace } from "../../../Utils/svelte-a11y";
    import { IconForbid, IconDots, IconUserPlus, IconSend } from "@wa-icons";

    interface Props {
        user: ChatUser;
        isMatrixChatEnabled?: boolean;
    }

    let { user, isMatrixChatEnabled = true }: Props = $props();

    let popoversElement: HTMLDivElement | undefined = $state();

    let buttonElement: HTMLButtonElement;

    let chatMenuActive = $derived($activeUserActionMenuStore === user.uuid);

    let usersByRoomStore:
        | Readable<Map<string | undefined, { roomName: string | undefined; users: ChatUser[] }>>
        | undefined = $state(undefined);

    let cleanup: undefined | (() => void);

    $effect(() => {
        if (popoversElement && buttonElement) {
            cleanup = autoUpdate(buttonElement, popoversElement, repositionIfOverflowing);
            return () => {
                cleanup?.();
                cleanup = undefined;
            };
        }
        return undefined;
    });
    let usersByRoomMap = $derived(usersByRoomStore && $usersByRoomStore ? $usersByRoomStore : new Map());
    // Flatten usersByRoomMap into a list of users with playUri from their room
    let usersWithRoomPlayUri = $derived(
        (() => {
            const usersList: (ChatUser & { playUri: string })[] = [];
            for (const [playUri, roomData] of usersByRoomMap.entries()) {
                for (const user of roomData.users) {
                    usersList.push({
                        ...user,
                        playUri: playUri ?? user.playUri ?? "",
                    });
                }
            }
            return usersList;
        })(),
    );
    let userToLocate = $derived(usersWithRoomPlayUri.find((u) => u.uuid === user.uuid));

    let isMe = $derived(
        user.chatId === localUserStore.getChatId() || user.uuid === localUserStore.getLocalUser()?.uuid,
    );

    let chatId = $derived(user.chatId);

    const { connection, roomUrl } = gameManager.getCurrentGameScene();

    let isInTheSameMap = $derived(user.playUri === roomUrl);

    const goTo = (type: string, playUri: string, uuid: string) => {
        analyticsClient.trackAdminEvent("user.go_to_clicked");

        if (type === "room") {
            scriptUtils.goToPage(`${playUri}#moveToUser=${uuid}`);
        } else if (type === "user") {
            if (user.uuid && connection && user.playUri) connection.emitAskPosition(user.uuid, user.playUri);
        }
    };

    function repositionIfOverflowing() {
        if (!buttonElement || !popoversElement) return;
        const currentPopoversElement = popoversElement;
        computePosition(buttonElement, currentPopoversElement, {
            middleware: [offset(6), flip(), shift({ padding: 5 })],
        })
            .then(({ x, y }) => {
                Object.assign(currentPopoversElement.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                });
            })
            .catch((error) => {
                console.error("Failed to compute popover position : ", error);
            });
    }

    const closeChatUserMenu = () => {
        activeUserActionMenuStore.set(null);
    };

    const toggleChatUSerMenu = () => {
        activeUserActionMenuStore.update((current) => (current === user.uuid ? null : (user.uuid ?? null)));
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (event.target && popoversElement && !popoversElement.contains(event.target as Node)) {
            closeChatUserMenu();
        }
    };

    onMount(() => {
        gameManager
            .getCurrentGameScene()
            .userProviderMerger.then((merger: UserProviderMerger) => {
                usersByRoomStore = merger.usersByRoomStore;
            })
            .catch((error) => {
                console.error("Failed to get users by room store : ", error);
            });
    });

    onDestroy(() => {
        if (cleanup) cleanup();
        activeUserActionMenuStore.set(null);
    });

    const showBusinessCard = (visitCardUrl: string | undefined) => {
        analyticsClient.trackAdminEvent("user.business_card.opened");

        // If woka menu is open, close it
        if ($wokaMenuStore) {
            wokaMenuStore.clear();
        }

        if (visitCardUrl) {
            if ($requestVisitCardsStore == visitCardUrl) {
                requestVisitCardsStore.set(null);
                closeChatUserMenu();
                return;
            }
            requestVisitCardsStore.set(visitCardUrl);
        }
        closeChatUserMenu();
    };

    function locateUser() {
        if (userToLocate == undefined || userToLocate.uuid == undefined) return;

        // Check if visit card url is the same as the current visit card url
        if ($requestVisitCardsStore != undefined) {
            requestVisitCardsStore.set(null);
        }

        // Track the open woka menu action
        analyticsClient.trackAdminEvent("user.woka_menu.opened");

        const currentScerne = gameManager.getCurrentGameScene();

        // Il user is in view port and represented by remote player, use it to activate the woka menu
        const remotePlayerData = currentScerne.getRemotePlayersRepository().getPlayerByUuid(userToLocate.uuid);
        if (remotePlayerData != undefined) {
            // Get the actual RemotePlayer sprite from MapPlayersByKey using userId
            const remotePlayer = currentScerne.MapPlayersByKey.get(remotePlayerData.userId);
            if (remotePlayer != undefined) {
                remotePlayer.activate();
                closeChatUserMenu();
                userListStore.toggle();
                return;
            }
        }

        // If the user isn't in the view port, emit the ask position message to the server
        currentScerne.connection?.emitAskPosition(
            userToLocate.uuid ?? "",
            userToLocate.playUri ?? "",
            AskPositionMessage_AskType.LOCATE,
        );
        userListStore.toggle();
        closeChatUserMenu();
    }
</script>

<svelte:window onclick={handleClickOutside} ontouchstart={handleClickOutside} />
<div class="wa-dropdown">
    <button
        class="m-0 py-px px-1.5 flex items-center rounded-md hover:bg-mdark/10 bg-transparent"
        bind:this={buttonElement}
        onclick={(event) => {
            event.stopPropagation();
            toggleChatUSerMenu();
        }}
        aria-label="Переключить опции пользователя"
    >
        <IconDots font-size="12" class="text-[#8B8E9C]" />
    </button>
    {#if chatMenuActive}
        <div
            bind:this={popoversElement}
            class="wa-dropdown-menu z-10 mr-1 fixed bg-mlight/80 shadow-light rounded-md2 backdrop-blur-md p-5 text-sm font-medium"
        >
            {#if isInTheSameMap}
                <span
                    role="button"
                    tabindex="0"
                    class="walk-to wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1"
                    onclick={(event) => {
                        event.stopPropagation();
                        goTo("user", user.playUri ?? "", user.uuid ?? "");
                        closeChatUserMenu();
                    }}
                    onkeydown={triggerClickOnEnterOrSpace}
                >
                    <WalkToIcon />
                    {$LL.chat.userList.walkTo()}
                </span>
                <span
                    role="button"
                    tabindex={userToLocate == undefined ? -1 : 0}
                    aria-disabled={userToLocate == undefined}
                    class={`follow wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1 ${
                        userToLocate == undefined ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
                    }`}
                    onclick={(event) => {
                        event.stopPropagation();
                        locateUser();
                    }}
                    onkeydown={triggerClickOnEnterOrSpace}
                >
                    <MapPinIcon />
                    {$LL.chat.userList.follow()}
                </span>
                <span
                    role="button"
                    tabindex="0"
                    class="invite wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1"
                    data-testid="user-list-invite"
                    onkeydown={triggerClickOnEnterOrSpace}
                    onclick={(event) => {
                        event.stopPropagation();
                        if (user.uuid) {
                            const scene = gameManager.getCurrentGameScene();
                            const sent = scene.inviteManager?.requestMeetingInvitation(user.uuid);
                            if (sent) {
                                try {
                                    scene.playSound("meeting-in", 0.15);
                                } catch (error) {
                                    console.error("Failed to play sound: ", error);
                                    Sentry.captureException(error);
                                }
                            }
                        }
                        closeChatUserMenu();
                    }}
                >
                    <UserAddIcon />
                    {$LL.chat.userList.invite()}
                </span>
            {:else if user.playUri}
                <span
                    role="button"
                    tabindex="0"
                    class="teleport wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1"
                    onkeydown={triggerClickOnEnterOrSpace}
                    onclick={(event) => {
                        event.stopPropagation();
                        goTo("room", user.playUri ?? "", user.uuid ?? "");
                        closeChatUserMenu();
                    }}
                    ><img
                        class="noselect"
                        src={teleport}
                        alt={$LL.chat.userList.teleport()}
                        height="13"
                        width="13"
                        draggable="false"
                    />
                    {$LL.chat.userList.teleport()}</span
                >
                <span
                    role="button"
                    tabindex="0"
                    class="invite wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 w-full rounded group mb-1"
                    data-testid="user-list-invite"
                    onkeydown={triggerClickOnEnterOrSpace}
                    onclick={(event) => {
                        event.stopPropagation();
                        if (user.uuid) {
                            const scene = gameManager.getCurrentGameScene();
                            const sent = scene.inviteManager?.requestMeetingInvitation(user.uuid);
                            if (sent) {
                                try {
                                    scene.playSound("meeting-in", 0.15);
                                } catch (error) {
                                    console.error("Failed to play sound: ", error);
                                    Sentry.captureException(error);
                                }
                            }
                        }
                        closeChatUserMenu();
                    }}
                >
                    <IconUserPlus class="w-4" />
                    {$LL.chat.userList.invite()}
                </span>
            {/if}
            {#if user.visitCardUrl}
                <span
                    role="button"
                    tabindex="0"
                    class="businessCard wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1"
                    onkeydown={triggerClickOnEnterOrSpace}
                    onclick={(event) => {
                        event.stopPropagation();
                        showBusinessCard(user.visitCardUrl);
                    }}
                    ><img
                        class="noselect"
                        src={businessCard}
                        alt={$LL.chat.userList.businessCard()}
                        height="13"
                        width="13"
                        draggable="false"
                    />
                    {$LL.chat.userList.businessCard()}</span
                >
            {/if}

            {#if user.username && user.uuid}
                <span
                    role="button"
                    tabindex="0"
                    class="ban wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group"
                    onkeydown={triggerClickOnEnterOrSpace}
                    onclick={(event) => {
                        event.stopPropagation();
                        openModerationModal(user.uuid ?? "", user.username ?? "");
                        closeChatUserMenu();
                    }}
                    ><IconForbid font-size="13" class="transition-all group-hover:text-mblue" />
                    {$LL.report.moderate.action()}</span
                >
            {/if}

            {#if !isMe && isMatrixChatEnabled && user.chatId}
                <button
                    type="button"
                    class="sendmessage wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/40 m-0 w-full rounded group mb-1 p-0"
                    data-testid="user-list-sendmessage"
                    onclick={(event) => {
                        event.stopPropagation();
                        openDirectChatRoom(chatId).catch((error) => {
                            console.error("Error opening direct chat room:", error);
                            Sentry.captureException(error, {
                                extra: {
                                    userId: user.uuid,
                                    chatId: chatId,
                                    playUri: user.playUri,
                                    username: user.username,
                                },
                            });
                        });
                        analyticsClient.trackAdminEvent("chat.message_from_user_list_clicked");
                    }}
                >
                    <IconSend font-size="16" class="group-hover:text-mblue" />
                    {$LL.chat.userList.sendMessage()}
                </button>
            {/if}
        </div>
    {/if}
</div>
